import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    ChevronRight, Loader2, Save, Pencil, Trash2, Plus, ShieldCheck, Network, X,
} from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";
import { MagneticButton } from "../components/MagneticButton";
import { getJSON, postJSON, patchJSON, delJSON } from "../lib/api";
import { usePageMeta } from "../lib/usePageMeta";
import { Paywall } from "../components/ipam/Paywall";
import { SubnetForm } from "../components/ipam/SubnetForm";
import { HardwareTab } from "../components/ipam/HardwareTab";
import { ImportantIPsTab } from "../components/ipam/ImportantIPsTab";
import { RecentConfigsModal } from "../components/ipam/RecentConfigsModal";
import { SEO } from "../components/SEO";

const VALID_TABS = ["profile", "subnets", "hardware", "important-ips", "notes"];

export const ClientDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    usePageMeta({ title: `Client · ${slug} — Cybersecurity Forefront` });

    const [client, setClient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [paywalled, setPaywalled] = useState(false);
    // Read tab from URL on first render. Falls back to "profile" if no/invalid value.
    const initialTab = (() => {
        const t = searchParams.get("tab");
        return t && VALID_TABS.includes(t) ? t : "profile";
    })();
    const [tab, setTab] = useState(initialTab);

    // Keep ?tab=... in sync with state so deep-links survive refresh + Back btn.
    useEffect(() => {
        const current = searchParams.get("tab");
        if (tab === "profile") {
            if (current) { searchParams.delete("tab"); setSearchParams(searchParams, { replace: true }); }
        } else if (current !== tab) {
            searchParams.set("tab", tab);
            setSearchParams(searchParams, { replace: true });
        }
        // eslint-disable-next-line
    }, [tab]);

    const reload = async () => {
        setLoading(true);
        try {
            const c = await getJSON(`/ipam/clients/${slug}`);
            setClient(c);
            setPaywalled(false);
        } catch (e) {
            if (e.status === 402) setPaywalled(true);
            else if (e.status === 404) {
                toast.error("Client not found");
                navigate("/clients");
            } else {
                toast.error(e.message || "Failed to load");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [slug]);

    if (paywalled) return <Paywall />;
    if (loading) {
        return (
            <div className="pt-28 mx-auto max-w-5xl px-5 text-text-muted flex items-center" data-testid="client-detail-loading">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
        );
    }
    if (!client) return null;

    return (
        <div className="pt-24 md:pt-28 pb-16 mx-auto max-w-5xl px-5 md:px-10" data-testid="client-detail-page">
            <SEO noindex title="Client detail" />
            {/* Breadcrumb */}
            <nav className="flex items-center text-[12px] text-text-muted gap-2" aria-label="Breadcrumb">
                <Link to="/clients" className="hover:text-accent-cyan transition-colors" data-testid="crumb-clients">
                    Clients
                </Link>
                <ChevronRight className="w-3 h-3 text-text-dim" />
                <span className="text-text-primary truncate">{client.name}</span>
            </nav>

            <Reveal>
                <div className="mt-5 flex items-center gap-3 flex-wrap">
                    <h1 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight" data-testid="client-detail-title">
                        {client.name}
                    </h1>
                    {client.nis2_in_scope && (
                        <span className="inline-flex items-center gap-1 text-[10.5px] font-mono uppercase tracking-[0.22em] px-2 py-0.5 rounded border border-accent-mint/30 text-accent-mint">
                            <ShieldCheck className="w-2.5 h-2.5" /> NIS2 in scope
                        </span>
                    )}
                    {client.status === "archived" && (
                        <span className="text-[10.5px] font-mono uppercase tracking-[0.22em] px-2 py-0.5 rounded border border-white/[0.1] text-text-dim">
                            Archived
                        </span>
                    )}
                </div>
                <div className="mt-2 text-[12px] text-text-muted flex items-center gap-3 flex-wrap">
                    {client.industry && <span>{client.industry}</span>}
                    <span className="text-text-dim">·</span>
                    <span>{client.subnet_count} {client.subnet_count === 1 ? "subnet" : "subnets"}</span>
                    {client.vat_number && <><span className="text-text-dim">·</span><span>VAT: {client.vat_number}</span></>}
                </div>
            </Reveal>

            {/* Tabs — scrollable horizontally on mobile so 5 entries don't break the layout */}
            <div
                className="mt-8 -mx-5 md:mx-0 px-5 md:px-0 overflow-x-auto scrollbar-thin"
                data-testid="client-tabs-scroll"
            >
                <div className="inline-flex rounded-md border border-white/[0.06] bg-surface/60 p-0.5 whitespace-nowrap" data-testid="client-tabs">
                    {[
                        { id: "profile", label: "Profile" },
                        { id: "subnets", label: "Subnets" },
                        { id: "hardware", label: "Hardware" },
                        { id: "important-ips", label: "Important IPs" },
                        { id: "notes", label: "Notes" },
                    ].map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setTab(t.id)}
                            className={`px-4 py-1.5 text-[12.5px] font-medium rounded-[5px] transition-colors ${
                                tab === t.id ? "bg-elevated text-text-primary" : "text-text-muted hover:text-text-primary"
                            }`}
                            data-testid={`client-tab-${t.id}`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="mt-6">
                {tab === "profile" && <ProfileTab client={client} onSaved={reload} />}
                {tab === "subnets" && <SubnetsTab client={client} onChanged={reload} />}
                {tab === "hardware" && <HardwareTab client={client} onChanged={reload} />}
                {tab === "important-ips" && <ImportantIPsTab client={client} onChanged={reload} />}
                {tab === "notes" && <NotesTab client={client} onSaved={reload} />}
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Profile tab — edit name, industry, contacts, address, VAT, NIS2 flag
// ---------------------------------------------------------------------------

const ProfileTab = ({ client, onSaved }) => {
    const [form, setForm] = useState({
        name: client.name || "",
        industry: client.industry || "",
        contact_name: client.contact_name || "",
        contact_email: client.contact_email || "",
        contact_phone: client.contact_phone || "",
        address: client.address || "",
        vat_number: client.vat_number || "",
        nis2_in_scope: !!client.nis2_in_scope,
    });
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    const update = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };

    const save = async () => {
        setSaving(true);
        try {
            await patchJSON(`/ipam/clients/${client.id}`, form);
            toast.success("Saved");
            setDirty(false);
            onSaved();
        } catch (e) {
            toast.error(e.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const F = ({ label, k, type = "text", placeholder, ...rest }) => (
        <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-text-dim">{label}</span>
            <input
                type={type}
                value={form[k]}
                placeholder={placeholder}
                onChange={(e) => update(k, e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-md bg-surface/60 border border-white/[0.06] text-text-primary text-[13px] placeholder:text-text-dim focus:outline-none focus:border-accent-cyan/40"
                data-testid={`client-field-${k}`}
                {...rest}
            />
        </label>
    );

    return (
        <div data-testid="client-profile-tab">
            <div className="grid gap-4 md:grid-cols-2">
                <F label="Name" k="name" placeholder="Studio Rossi SRL" />
                <F label="Industry" k="industry" placeholder="Healthcare, Manufacturing, …" />
                <F label="Contact name" k="contact_name" />
                <F label="Contact email" k="contact_email" type="email" placeholder="info@client.com" />
                <F label="Contact phone" k="contact_phone" />
                <F label="VAT number" k="vat_number" placeholder="IT01234567890" />
                <div className="md:col-span-2">
                    <F label="Address" k="address" placeholder="Street, City, ZIP, Country" />
                </div>
                <label className="md:col-span-2 inline-flex items-center gap-2 mt-1 cursor-pointer select-none">
                    <input
                        type="checkbox"
                        checked={form.nis2_in_scope}
                        onChange={(e) => update("nis2_in_scope", e.target.checked)}
                        className="accent-accent-cyan"
                        data-testid="client-field-nis2"
                    />
                    <span className="text-[12.5px] text-text-muted">
                        Mark this client as <span className="text-accent-mint">NIS2 in scope</span> (regulated entity).
                    </span>
                </label>
            </div>
            <div className="mt-6 flex items-center gap-3">
                <MagneticButton
                    type="button"
                    onClick={save}
                    disabled={!dirty || saving}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-accent-cyan text-canvas text-[13px] font-medium hover:shadow-glow-cyan disabled:opacity-40"
                    ariaLabel="Save client"
                    data-testid="client-profile-save"
                >
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    {saving ? "Saving…" : "Save changes"}
                </MagneticButton>
                {!dirty && <span className="text-[11px] text-text-dim">No unsaved changes</span>}
            </div>

            <RecentConfigsCard client={client} />
        </div>
    );
};

// ---------------------------------------------------------------------------
// Phase 7c.4 — Recent configs card in the Profile tab
// ---------------------------------------------------------------------------

const RecentConfigsCard = ({ client }) => {
    const [count, setCount] = useState(null);
    const [latest, setLatest] = useState(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        let cancelled = false;
        getJSON(`/ipam/clients/${client.id}/saved-configs`)
            .then((d) => {
                if (cancelled) return;
                setCount(d.total || 0);
                setLatest(d.items?.[0] || null);
            })
            .catch(() => { if (!cancelled) setCount(0); });
        return () => { cancelled = true; };
    }, [client.id]);

    return (
        <div
            className="mt-10 p-4 rounded-xl border border-white/[0.06] bg-surface/40 flex items-center gap-4"
            data-testid="recent-configs-card"
        >
            <div className="w-9 h-9 rounded-md bg-accent-cyan/[0.08] border border-accent-cyan/30 flex items-center justify-center">
                <ShieldCheck className="w-4 h-4 text-accent-cyan" />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-[10.5px] font-mono uppercase tracking-[0.18em] text-text-dim">
                    ZeroConf · Recent configs
                </p>
                <p className="text-[13px] text-text-primary mt-0.5">
                    {count === null
                        ? "Loading…"
                        : count === 0
                            ? "No saved configurations yet for this client"
                            : `${count} saved · most recent ${latest?.name || "Untitled draft"}`}
                </p>
            </div>
            <div className="flex items-center gap-2">
                {count > 0 && (
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        className="px-3 py-1.5 rounded-md border border-white/[0.08] text-text-muted hover:text-text-primary text-[12px]"
                        data-testid="recent-configs-view-all"
                    >
                        View all
                    </button>
                )}
                <Link
                    to="/portal"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent-cyan text-canvas text-[12px] font-medium hover:shadow-glow-cyan"
                    data-testid="recent-configs-new"
                >
                    <Plus className="w-3 h-3" />
                    {count > 0 ? "New config" : "Run ZeroConf"}
                </Link>
            </div>
            {open && <RecentConfigsModal client={client} onClose={() => setOpen(false)} />}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Subnets tab — list + create + edit + delete with CIDR live preview
// ---------------------------------------------------------------------------

const SubnetsTab = ({ client, onChanged }) => {
    const [subnets, setSubnets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null = none, "new" = new, else subnet obj

    const reload = async () => {
        setLoading(true);
        try {
            const r = await getJSON(`/ipam/clients/${client.id}/subnets`);
            setSubnets(r.items || []);
        } catch (e) {
            toast.error(e.message || "Failed to load subnets");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [client.id]);

    const remove = async (s) => {
        if (!window.confirm(`Delete subnet ${s.cidr} (${s.name})? This cannot be undone.`)) return;
        try {
            await delJSON(`/ipam/subnets/${s.id}`);
            toast.success("Subnet deleted");
            await reload();
            onChanged();
        } catch (e) {
            toast.error(e.message || "Delete failed");
        }
    };

    return (
        <div data-testid="client-subnets-tab">
            <div className="flex items-center justify-between">
                <p className="text-[13px] text-text-muted">
                    {loading ? "…" : `${subnets.length} ${subnets.length === 1 ? "subnet" : "subnets"} on file`}
                </p>
                <MagneticButton
                    type="button"
                    onClick={() => setEditing("new")}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent-cyan text-canvas text-[12.5px] font-medium hover:shadow-glow-cyan"
                    ariaLabel="Add subnet"
                    data-testid="subnet-add-btn"
                >
                    <Plus className="w-3.5 h-3.5" /> Add subnet
                </MagneticButton>
            </div>

            <div className="mt-4 grid gap-2">
                {loading && <p className="text-text-muted text-[12px]">Loading…</p>}
                {!loading && subnets.length === 0 && (
                    <p className="text-text-dim text-[12.5px]">
                        No subnets yet. Click <span className="text-accent-cyan">Add subnet</span> to record your first one.
                    </p>
                )}
                {subnets.map((s) => (
                    <div
                        key={s.id}
                        className="rounded-lg border border-white/[0.06] bg-surface/40 hover:border-accent-cyan/30 transition-colors p-3 flex items-center gap-3 flex-wrap"
                        data-testid={`subnet-row-${s.id}`}
                    >
                        <Network className="w-3.5 h-3.5 text-accent-cyan shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-mono text-[13px] text-text-primary">{s.cidr}</span>
                                <span className="text-text-dim">·</span>
                                <span className="text-[12.5px] text-text-muted truncate">{s.name}</span>
                                <span className="text-[10px] font-mono uppercase tracking-[0.22em] px-1.5 py-0.5 rounded border border-white/[0.08] text-text-dim">{s.purpose}</span>
                                {s.vlan_id != null && (
                                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-white/[0.08] text-text-muted">VLAN {s.vlan_id}</span>
                                )}
                            </div>
                            <div className="mt-1 text-[11.5px] text-text-dim flex flex-wrap gap-3">
                                {s.gateway && <span>GW {s.gateway}</span>}
                                {s.dns_servers?.length > 0 && <span>DNS {s.dns_servers.join(", ")}</span>}
                                {s.site && <span>{s.site}</span>}
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <button
                                type="button"
                                onClick={() => setEditing(s)}
                                className="inline-flex items-center px-2 py-1 rounded border border-white/[0.06] text-[11px] text-text-muted hover:text-accent-cyan hover:border-accent-cyan/30"
                                data-testid={`subnet-edit-${s.id}`}
                            >
                                <Pencil className="w-3 h-3" />
                            </button>
                            <button
                                type="button"
                                onClick={() => remove(s)}
                                className="inline-flex items-center px-2 py-1 rounded border border-white/[0.06] text-[11px] text-text-muted hover:text-accent-red hover:border-accent-red/30"
                                data-testid={`subnet-delete-${s.id}`}
                            >
                                <Trash2 className="w-3 h-3" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {editing !== null && (
                <SubnetForm
                    client={client}
                    subnet={editing === "new" ? null : editing}
                    onClose={() => setEditing(null)}
                    onSaved={async () => { setEditing(null); await reload(); onChanged(); }}
                />
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Notes tab — plain textarea (markdown shows as raw text for 7c.1 base)
// ---------------------------------------------------------------------------

const NotesTab = ({ client, onSaved }) => {
    const [notes, setNotes] = useState(client.notes || "");
    const [saving, setSaving] = useState(false);
    const dirty = notes !== (client.notes || "");

    const save = async () => {
        setSaving(true);
        try {
            await patchJSON(`/ipam/clients/${client.id}`, { notes });
            toast.success("Notes saved");
            onSaved();
        } catch (e) {
            toast.error(e.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="client-notes-tab">
            <p className="text-[12px] text-text-muted">Markdown-friendly free-form notes. Shown as plain text for now.</p>
            <textarea
                rows={12}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="# Background&#10;Key contacts, special requirements, network quirks…"
                className="mt-3 w-full px-3 py-2 rounded-md bg-surface/60 border border-white/[0.06] text-text-primary text-[13px] font-mono placeholder:text-text-dim focus:outline-none focus:border-accent-cyan/40"
                data-testid="client-notes-textarea"
            />
            <div className="mt-4">
                <MagneticButton
                    type="button"
                    onClick={save}
                    disabled={!dirty || saving}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-accent-cyan text-canvas text-[13px] font-medium hover:shadow-glow-cyan disabled:opacity-40"
                    ariaLabel="Save notes"
                    data-testid="client-notes-save"
                >
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    {saving ? "Saving…" : "Save notes"}
                </MagneticButton>
            </div>
        </div>
    );
};

export default ClientDetail;
